/**
 * The public board's own vocabulary: cities, airlines, desks, belts and what
 * the hall is told.
 *
 * `fids.ts` decides WHAT a movement's status is. This file decides how a
 * passenger reads it — and every rule here is again a rule about what the
 * board must not say.
 *
 * ── a code is not a place ───────────────────────────────────────────────
 *
 * The record carries an aerodrome code (OEJN, or JED from a feed that sends
 * IATA). Nobody in the hall reads OEJN. The board prints the city, in English
 * and Arabic, and where the code is not in the list it prints the code and
 * the operator's list says so — it never guesses a city.
 *
 * ── desks and belts come from the plan, not the board ───────────────────
 *
 * Check-in desks and reclaim belts are placed by the allocation engine. The
 * board reads its plan; it has no way to set one. A flight the plan has not
 * placed shows nothing, and the operator's list says that too.
 *
 * ── two languages, the same words ───────────────────────────────────────
 *
 * A hall in the Kingdom reads in Arabic and English. The board alternates
 * between them whatever language the console is in, so the Arabic comes from
 * the dictionary directly, not through the console's current language.
 *
 * Pure. No React, and `now` is always passed in.
 */
import { AR, type Lang } from "../i18n";
import { PARAMS, type Assignment, type Demand } from "./allocation";
import { bestKnown, flightName, type Flight } from "./flights";
import { GATE_CLOSED_FROM, type FidsRow } from "./fids";

/* ── the words, in a chosen language ──────────────────────────────────── */

/**
 * Say an English sentence in the board's language, not the console's. Every
 * sentence passed here has an entry in `ar/flights.ts`; a test holds that.
 */
export function say(lang: Lang, en: string, vars?: Record<string, string | number>): string {
  const text = lang === "ar" ? (AR[en] ?? en) : en;
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, k: string) => (k in vars ? String(vars[k]) : whole));
}

/** Every sentence the public board says, so the test can check each has Arabic. */
export const BOARD_WORDS = [
  "Departures",
  "Arrivals",
  "Time",
  "Flight",
  "To",
  "From",
  "Expected",
  "Check-in",
  "Gate",
  "Belt",
  "Status",
  "to follow",
  "by bus from the gate",
  "Welcome to {city}",
  "{flight} to {city} is boarding at gate {gate}.",
  "Final call for {flight} to {city} at gate {gate}.",
  "{flight} to {city} is cancelled. Please contact your airline.",
  "For {flight} please ask a member of staff.",
  "{flight} to {city} is delayed. A new time will follow.",
  "{flight} from {city} has landed. Baggage on {belt}.",
  "Check-in for {flight} to {city} is open at desks {desks}.",
  "Gate closes in {n} min",
  "Gate closed",
  "Boarding",
  "Final call",
  "Next at this gate",
  "No departure is due at this gate.",
  "No flight is due on this belt.",
  "Landed {time}",
  "Expected {time}",
  "Page {n} of {of}",
] as const;

/* ── places ───────────────────────────────────────────────────────────── */

export interface Place {
  en: string;
  ar: string;
  iata: string;
}

/**
 * The cities the airport's network reaches, keyed by ICAO with the IATA code
 * alongside so a feed sending either is understood. Add to it; never guess.
 */
const PLACES: Record<string, Place> = {
  OEMA: { en: "Madinah", ar: "المدينة المنورة", iata: "MED" },
  OEJN: { en: "Jeddah", ar: "جدة", iata: "JED" },
  OERK: { en: "Riyadh", ar: "الرياض", iata: "RUH" },
  OEDF: { en: "Dammam", ar: "الدمام", iata: "DMM" },
  OEAB: { en: "Abha", ar: "أبها", iata: "AHB" },
  OEGS: { en: "Qassim", ar: "القصيم", iata: "ELQ" },
  OETB: { en: "Tabuk", ar: "تبوك", iata: "TUU" },
  OETF: { en: "Taif", ar: "الطائف", iata: "TIF" },
  OEYN: { en: "Yanbu", ar: "ينبع", iata: "YNB" },
  OEGN: { en: "Jazan", ar: "جازان", iata: "GIZ" },
  OEHL: { en: "Hail", ar: "حائل", iata: "HAS" },
  OEAH: { en: "Al-Ahsa", ar: "الأحساء", iata: "HOF" },
  OENG: { en: "Najran", ar: "نجران", iata: "EAM" },
  OEPA: { en: "Al Qaisumah", ar: "القيصومة", iata: "AQI" },
  OESK: { en: "Al Jouf", ar: "الجوف", iata: "AJF" },
  OEAO: { en: "AlUla", ar: "العلا", iata: "ULH" },
  OEBH: { en: "Bisha", ar: "بيشة", iata: "BHH" },
  OKKK: { en: "Kuwait", ar: "الكويت", iata: "KWI" },
  OTHH: { en: "Doha", ar: "الدوحة", iata: "DOH" },
  OMDB: { en: "Dubai", ar: "دبي", iata: "DXB" },
  OMAA: { en: "Abu Dhabi", ar: "أبوظبي", iata: "AUH" },
  OMSJ: { en: "Sharjah", ar: "الشارقة", iata: "SHJ" },
  OBBI: { en: "Bahrain", ar: "البحرين", iata: "BAH" },
  OOMS: { en: "Muscat", ar: "مسقط", iata: "MCT" },
  OJAI: { en: "Amman", ar: "عمّان", iata: "AMM" },
  OLBA: { en: "Beirut", ar: "بيروت", iata: "BEY" },
  ORBI: { en: "Baghdad", ar: "بغداد", iata: "BGW" },
  HECA: { en: "Cairo", ar: "القاهرة", iata: "CAI" },
  HEAX: { en: "Alexandria", ar: "الإسكندرية", iata: "HBE" },
  HSSK: { en: "Khartoum", ar: "الخرطوم", iata: "KRT" },
  HAAB: { en: "Addis Ababa", ar: "أديس أبابا", iata: "ADD" },
  DAAG: { en: "Algiers", ar: "الجزائر", iata: "ALG" },
  DTTA: { en: "Tunis", ar: "تونس", iata: "TUN" },
  GMMN: { en: "Casablanca", ar: "الدار البيضاء", iata: "CMN" },
  LTFM: { en: "Istanbul", ar: "إسطنبول", iata: "IST" },
  LTFJ: { en: "Istanbul Sabiha", ar: "إسطنبول صبيحة", iata: "SAW" },
  LTAI: { en: "Antalya", ar: "أنطاليا", iata: "AYT" },
  OPLA: { en: "Lahore", ar: "لاهور", iata: "LHE" },
  OPKC: { en: "Karachi", ar: "كراتشي", iata: "KHI" },
  OPIS: { en: "Islamabad", ar: "إسلام آباد", iata: "ISB" },
  OPPS: { en: "Peshawar", ar: "بيشاور", iata: "PEW" },
  OPMT: { en: "Multan", ar: "ملتان", iata: "MUX" },
  VGHS: { en: "Dhaka", ar: "دكا", iata: "DAC" },
  VIDP: { en: "Delhi", ar: "دلهي", iata: "DEL" },
  VABB: { en: "Mumbai", ar: "مومباي", iata: "BOM" },
  VOHS: { en: "Hyderabad", ar: "حيدر آباد", iata: "HYD" },
  VOCI: { en: "Kochi", ar: "كوتشي", iata: "COK" },
  VCBI: { en: "Colombo", ar: "كولومبو", iata: "CMB" },
  WIII: { en: "Jakarta", ar: "جاكرتا", iata: "CGK" },
  WARR: { en: "Surabaya", ar: "سورابايا", iata: "SUB" },
  WMKK: { en: "Kuala Lumpur", ar: "كوالالمبور", iata: "KUL" },
  UTTT: { en: "Tashkent", ar: "طشقند", iata: "TAS" },
  OAKB: { en: "Kabul", ar: "كابول", iata: "KBL" },
  OIIE: { en: "Tehran", ar: "طهران", iata: "IKA" },
  EGLL: { en: "London", ar: "لندن", iata: "LHR" },
  LFPG: { en: "Paris", ar: "باريس", iata: "CDG" },
};

const BY_IATA = new Map(Object.values(PLACES).map((p) => [p.iata, p]));

/** The city for an aerodrome code, ICAO or IATA; null when it is not in the list. */
export function placeFor(code: string | undefined | null): Place | null {
  if (!code) return null;
  const k = code.trim().toUpperCase();
  return PLACES[k] ?? BY_IATA.get(k) ?? null;
}

/* ── airlines ─────────────────────────────────────────────────────────── */

export interface Airline {
  en: string;
  ar: string;
}

const AIRLINES: Record<string, Airline> = {
  SV: { en: "Saudia", ar: "السعودية" },
  XY: { en: "flynas", ar: "طيران ناس" },
  F3: { en: "flyadeal", ar: "طيران أديل" },
  RX: { en: "Riyadh Air", ar: "طيران الرياض" },
  MS: { en: "EgyptAir", ar: "مصر للطيران" },
  GF: { en: "Gulf Air", ar: "طيران الخليج" },
  J9: { en: "Jazeera Airways", ar: "طيران الجزيرة" },
  KU: { en: "Kuwait Airways", ar: "الخطوط الجوية الكويتية" },
  FZ: { en: "flydubai", ar: "فلاي دبي" },
  EK: { en: "Emirates", ar: "طيران الإمارات" },
  EY: { en: "Etihad", ar: "الاتحاد للطيران" },
  G9: { en: "Air Arabia", ar: "العربية للطيران" },
  QR: { en: "Qatar Airways", ar: "الخطوط الجوية القطرية" },
  WY: { en: "Oman Air", ar: "الطيران العماني" },
  RJ: { en: "Royal Jordanian", ar: "الملكية الأردنية" },
  TK: { en: "Turkish Airlines", ar: "الخطوط الجوية التركية" },
  PC: { en: "Pegasus", ar: "بيغاسوس" },
  PK: { en: "PIA", ar: "الخطوط الجوية الباكستانية" },
  PA: { en: "Airblue", ar: "إير بلو" },
  UL: { en: "SriLankan", ar: "السريلانكية" },
  GA: { en: "Garuda Indonesia", ar: "غارودا إندونيسيا" },
  MH: { en: "Malaysia Airlines", ar: "الخطوط الماليزية" },
  BG: { en: "Biman", ar: "بيمان" },
  AI: { en: "Air India", ar: "طيران الهند" },
  "6E": { en: "IndiGo", ar: "إنديغو" },
  HY: { en: "Uzbekistan Airways", ar: "الخطوط الأوزبكية" },
  ET: { en: "Ethiopian", ar: "الإثيوبية" },
  SD: { en: "Sudan Airways", ar: "الخطوط السودانية" },
  AH: { en: "Air Algérie", ar: "الخطوط الجوية الجزائرية" },
  TU: { en: "Tunisair", ar: "الخطوط التونسية" },
  AT: { en: "Royal Air Maroc", ar: "الخطوط الملكية المغربية" },
};

export function airlineFor(code: string | undefined): Airline | null {
  return code ? (AIRLINES[code.toUpperCase()] ?? null) : null;
}

/**
 * A steady colour for a carrier's tile, from its code. Not the airline's
 * livery: the board identifies a carrier by its code and name, and does not
 * borrow anybody's brand.
 */
export function carrierHue(code: string | undefined): number {
  let h = 0;
  for (const ch of code ?? "") h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
}

/* ── desks and belts, from the allocation plan ────────────────────────── */

export interface PlannedPlaces {
  /** check-in desks for a departure, e.g. ["C01", "C02"] */
  counters?: string[];
  /** reclaim belt for an arrival */
  belt?: string;
}

/**
 * The allocation plan's desks and belts, by flight name and direction.
 *
 * Matched by name, not id: the plan is built from the flight record and the
 * board from the movements, and a name is what both of them agree on. A
 * desk span "C01–C04" is what a passenger looks for in the hall.
 */
export function plannedPlaces(
  demands: readonly Demand[] | undefined,
  plan: readonly Assignment[] | undefined,
): Map<string, PlannedPlaces> {
  const out = new Map<string, PlannedPlaces>();
  if (!demands || !plan) return out;
  const byKey = new Map(plan.map((a) => [a.key, a]));
  for (const d of demands) {
    if (d.kind !== "counter" && d.kind !== "belt") continue;
    const a = byKey.get(d.key);
    if (!a) continue;
    const codes = [...new Set(a.segments.flatMap((s) => s.codes))];
    if (!codes.length) continue;
    const k = `${d.direction}:${d.label}`;
    const cur = out.get(k) ?? {};
    if (d.kind === "counter") cur.counters = codes.sort();
    else cur.belt = codes[0];
    out.set(k, cur);
  }
  return out;
}

/** "C01–C04" for a run, "C01, C05" otherwise. */
export function deskSpan(codes: readonly string[] | undefined): string {
  if (!codes?.length) return "";
  if (codes.length === 1) return codes[0];
  const nums = codes.map((c) => Number(c.replace(/\D/g, "")));
  const prefix = codes[0].replace(/\d+$/, "");
  const run = nums.every((n, i) => i === 0 || n === nums[i - 1] + 1) && codes.every((c) => c.startsWith(prefix));
  return run ? `${codes[0]}–${codes.at(-1)}` : codes.join(", ");
}

/* ── the public row ───────────────────────────────────────────────────── */

export interface PublicRow extends FidsRow {
  city: Place | null;
  airline: Airline | null;
  counters?: string[];
  belt?: string;
  /** the best-known time, for the gate screen's countdown */
  bestAt: string | null;
}

/**
 * Whether check-in is open for a departure now. Desks open and close on the
 * SCHEDULE, as the allocation engine places them: people come to the airport
 * by the ticket, not the prediction.
 */
export function checkInOpen(scheduled: string | null, now: Date): boolean {
  if (!scheduled) return false;
  const min = (Date.parse(scheduled) - now.getTime()) / 60_000;
  return min > PARAMS.counterCloseBefore && min <= PARAMS.counterOpenBefore;
}

export function publicRows(
  rows: readonly FidsRow[],
  flights: readonly Flight[],
  places: Map<string, PlannedPlaces>,
  now: Date,
): PublicRow[] {
  const byId = new Map(flights.map((f) => [f.id, f]));
  return rows.map((r) => {
    const f = byId.get(r.flightId);
    const planned = f ? places.get(`${r.direction}:${flightName(f)}`) : undefined;
    return {
      ...r,
      city: placeFor(f?.otherAirport),
      airline: airlineFor(r.carrier),
      /* a cancelled flight checks nobody in and delivers no bags; a closed desk is not printed as open */
      counters: r.status === "cancelled" || !checkInOpen(r.scheduled, now) ? undefined : planned?.counters,
      belt: r.status === "cancelled" ? undefined : planned?.belt,
      bestAt: f ? bestKnown(f).at ?? null : null,
    };
  });
}

/** The city as the board prints it: the name, or the code when it is not in the list. */
export function cityName(r: PublicRow, lang: Lang): string {
  return r.city ? r.city[lang] : r.place;
}

/* ── what the hall is told ────────────────────────────────────────────── */

/**
 * The announcements line, derived from the same rows. Only what a passenger
 * can act on: boarding, final call, cancelled (never why), a delay with no
 * time yet, open desks, and the belt a landed flight's bags come to.
 */
export function announcements(rows: readonly PublicRow[], lang: Lang, site: string): string[] {
  const out: string[] = [];
  const home = placeFor(site);
  out.push(say(lang, "Welcome to {city}", { city: home ? home[lang] : site }));
  const order: Partial<Record<FidsRow["status"], number>> = { final_call: 0, boarding: 1, cancelled: 2, see_staff: 3, delayed: 4 };
  const sorted = [...rows].sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9));
  for (const r of sorted) {
    const city = cityName(r, lang);
    const v = { flight: r.flight, city, gate: r.gate ?? "", belt: r.belt ?? "", desks: deskSpan(r.counters) };
    if (r.direction === "departure") {
      if (r.status === "final_call" && r.gate) out.push(say(lang, "Final call for {flight} to {city} at gate {gate}.", v));
      else if (r.status === "boarding" && r.gate) out.push(say(lang, "{flight} to {city} is boarding at gate {gate}.", v));
      else if (r.status === "cancelled") out.push(say(lang, "{flight} to {city} is cancelled. Please contact your airline.", v));
      else if (r.status === "see_staff") out.push(say(lang, "For {flight} please ask a member of staff.", v));
      else if (r.status === "delayed" && !r.shown) out.push(say(lang, "{flight} to {city} is delayed. A new time will follow.", v));
      else if ((r.status === "on_time" || r.status === "scheduled") && r.counters?.length)
        out.push(say(lang, "Check-in for {flight} to {city} is open at desks {desks}.", v));
    } else if (r.status === "landed" && r.belt) {
      out.push(say(lang, "{flight} from {city} has landed. Baggage on {belt}.", v));
    }
  }
  return out;
}

/* ── the gate screen ──────────────────────────────────────────────────── */

export interface GateScreen {
  current: PublicRow | null;
  /** whole minutes until the gate closes, while boarding; null otherwise */
  closesIn: number | null;
  next: PublicRow[];
}

/**
 * What the screen above one gate shows: the departure it is serving now —
 * the earliest that has not left and is not cancelled — and the next ones.
 */
export function gateScreen(rows: readonly PublicRow[], gate: string, now: Date): GateScreen {
  const here = rows.filter(
    (r) => r.direction === "departure" && r.gate === gate && r.status !== "departed" && r.status !== "cancelled",
  );
  const [current, ...next] = here;
  let closesIn: number | null = null;
  if (current?.bestAt && (current.status === "boarding" || current.status === "final_call")) {
    const n = Math.floor((Date.parse(current.bestAt) - GATE_CLOSED_FROM * 60_000 - now.getTime()) / 60_000);
    closesIn = Math.max(0, n);
  }
  return { current: current ?? null, closesIn, next: next.slice(0, 3) };
}

/** Every gate a departure on the board uses, in gate order. */
export function boardGates(rows: readonly PublicRow[]): string[] {
  const gates = [...new Set(rows.filter((r) => r.direction === "departure" && r.gate).map((r) => r.gate!))];
  return gates.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

/* ── the belt screen ──────────────────────────────────────────────────── */

/** The arrivals whose bags come to one belt, landed first. */
export function beltScreen(rows: readonly PublicRow[], belt: string): PublicRow[] {
  return rows.filter((r) => r.direction === "arrival" && r.belt === belt && r.status !== "cancelled");
}

export function boardBelts(rows: readonly PublicRow[]): string[] {
  const belts = [...new Set(rows.filter((r) => r.direction === "arrival" && r.belt).map((r) => r.belt!))];
  return belts.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

/* ── what the operator should know about the public words ─────────────── */

export interface PublicGaps {
  /** the code is not in the city list, so the board prints the code */
  noCity: PublicRow[];
  /** a departure whose check-in should be open now, with no desks in the plan */
  noDesks: PublicRow[];
  /** an arrival landed or due within the hour with no belt in the plan */
  noBelt: PublicRow[];
}

export function publicGaps(rows: readonly PublicRow[], now: Date): PublicGaps {
  const within = (r: PublicRow, min: number) =>
    r.bestAt !== null && Date.parse(r.bestAt) - now.getTime() <= min * 60_000;
  const live = rows.filter((r) => r.status !== "cancelled");
  return {
    noCity: live.filter((r) => !r.city),
    noDesks: live.filter(
      (r) => r.direction === "departure" && !r.counters?.length && r.status !== "departed" && checkInOpen(r.scheduled, now),
    ),
    noBelt: live.filter((r) => r.direction === "arrival" && !r.belt && (r.status === "landed" || within(r, 60))),
  };
}
